import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, FilePen } from 'lucide-react';
import { OwnerShell } from '../OwnerShell';
import { StatusBadge } from '../../common/StatusBadge';
import { docTone, ownerDocuments, ownerVehicles } from '../../../data/ownerProfile';
import { fromBn, toBn } from '../../../utils/bn';
import type { OwnerNav } from '../../../types/owner';
const months = [{
  name: 'অক্টোবর ২০২৬',
  m: 10,
  start: 4,
  days: 31
}, {
  name: 'নভেম্বর ২০২৬',
  m: 11,
  start: 0,
  days: 30
}, {
  name: 'ডিসেম্বর ২০২৬',
  m: 12,
  start: 2,
  days: 31
}];
const weekdays = ['রবি', 'সোম', 'মঙ্গল', 'বুধ', 'বৃহঃ', 'শুক্র', 'শনি'];
type Filter = '7' | '30' | '60' | 'expired';
const filters: {
  key: Filter;
  label: string;
}[] = [{
  key: '7',
  label: '৭ দিনের মধ্যে'
}, {
  key: '30',
  label: '৩০ দিনের মধ্যে'
}, {
  key: '60',
  label: '৬০ দিনের মধ্যে'
}, {
  key: 'expired',
  label: 'মেয়াদোত্তীর্ণ'
}];
const allDocs = Object.entries(ownerDocuments).flatMap(([vid, docs]) => docs.filter(d => d.daysLeft !== null).map(d => {
  const [dd, mm, yy] = fromBn(d.expiry).split('/').map(Number);
  return {
    ...d,
    vid,
    reg: ownerVehicles.find(v => v.id === vid)?.reg ?? '',
    day: dd,
    month: mm,
    year: yy
  };
}));
export function OwnerExpiry({
  nav
}: {
  nav: OwnerNav;
}) {
  const [mi, setMi] = useState(0);
  const [filter, setFilter] = useState<Filter>('60');
  const mo = months[mi];
  const marks = allDocs.filter(d => d.year === 2026 && d.month === mo.m);
  const list = allDocs.filter(d => {
    const n = d.daysLeft ?? 9999;
    if (filter === 'expired') return n < 0;
    return n >= 0 && n <= Number(filter);
  }).sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0));
  return <OwnerShell nav={nav} title="মেয়াদ সংক্রান্ত সতর্কতা" data-id="element-1567" data-mp="1rurnc2gdkxhh">
      <section className="rounded-xl border border-border bg-white p-3 shadow-sm" aria-label="মেয়াদ ক্যালেন্ডার" data-id="element-1568" data-mp="3oo3966kx3622">
        <div className="mb-2 flex items-center justify-between" data-id="element-1569" data-mp="3tnphc1lw1w40">
          <button onClick={() => setMi(m => Math.max(0, m - 1))} disabled={mi === 0} aria-label="আগের মাস" className="rounded-full p-1.5 text-navy-800 disabled:opacity-30" data-id="element-1570" data-mp="3llowi13f8cl8"><ChevronLeft className="h-4 w-4" data-id="element-1571" data-mp="3hm6bz6fii3ix" /></button>
          <p className="text-sm font-semibold text-navy-900" data-id="element-1572" data-mp="3vkehi2qm6d5p">{mo.name}</p>
          <button onClick={() => setMi(m => Math.min(months.length - 1, m + 1))} disabled={mi === months.length - 1} aria-label="পরের মাস" className="rounded-full p-1.5 text-navy-800 disabled:opacity-30" data-id="element-1573" data-mp="3ql1jqmy7floc"><ChevronRight className="h-4 w-4" data-id="element-1574" data-mp="4dtjfz2564z6" /></button>
        </div>
        <div className="grid grid-cols-7 gap-y-1 text-center text-[11px]" data-id="element-1575" data-mp="4dl4m1mgkplc">
          {weekdays.map(w => <span key={w} className="pb-1 font-medium text-slate-400" data-id="element-1576" data-mp="i4q5t85h5pxs">{w}</span>)}
          {Array.from({
          length: mo.start
        }).map((_, i) => <span key={`e${i}`} data-id="element-1577" data-mp="d8hq9tnd7uq3" data-mpi={i} />)}
          {Array.from({
          length: mo.days
        }).map((_, i) => {
          const day = i + 1;
          const m = marks.find(x => x.day === day);
          const today = mo.m === 10 && day === 13;
          const tone = m ? docTone(m.daysLeft).tone : null;
          const cls = tone === 'red' ? 'bg-red-600 text-white' : tone === 'orange' ? 'bg-amber-500 text-white' : tone === 'green' ? 'bg-emerald-600 text-white' : today ? 'ring-2 ring-navy-700 text-navy-900 font-bold' : 'text-slate-700';
          return <span key={day} className="flex justify-center" title={m ? `${m.name} — ${m.reg}` : undefined} data-id="element-1578" data-mp="11pqjty0yv1sq" data-mpi={i}>
                <span className={`flex h-7 w-7 items-center justify-center rounded-full ${cls}`} data-id="element-1579" data-mp="n0ubdzuzzmrl">{toBn(day)}</span>
              </span>;
        })}
        </div>
        <p className="mt-2 text-center text-[10.5px] text-slate-400" data-id="element-1580" data-mp="e2ia93wi96kw">ঘেরা তারিখ = আজ (১৩ অক্টোবর) • রঙিন তারিখ = মেয়াদ শেষের দিন</p>
      </section>

      <div className="no-scrollbar -mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4" role="tablist" data-id="element-1581" data-mp="iy5zfjazlzqe">
        {filters.map(f => <button key={f.key} role="tab" aria-selected={filter === f.key} onClick={() => setFilter(f.key)} className={`shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${filter === f.key ? f.key === 'expired' ? 'border-red-600 bg-red-600 text-white' : 'border-navy-800 bg-navy-800 text-white' : 'border-slate-200 bg-white text-slate-600'}`} data-id="element-1582" data-mp="92vwmtcrlobk">
            {f.label}
          </button>)}
      </div>

      <div className="mt-3 space-y-2.5" data-id="element-1583" data-mp="xnko1ed0gtry">
        {list.map(d => {
        const t = docTone(d.daysLeft);
        const bg = {
          red: 'border-red-200 bg-red-50',
          orange: 'border-amber-200 bg-amber-50',
          green: 'border-emerald-200 bg-emerald-50'
        }[t.tone];
        return <div key={d.vid + d.key} className={`rounded-xl border p-3 ${bg}`} data-id="element-1584" data-mp="jnt4ky3q6l6k">
              <div className="flex items-start justify-between gap-2" data-id="element-1585" data-mp="eprlid6aulv3">
                <div data-id="element-1586" data-mp="erm4hly0g3wj">
                  <p className="text-[14px] font-semibold text-navy-900" data-id="element-1587" data-mp="2e2dkyyl0juk5">{d.name}</p>
                  <p className="text-[11.5px] text-slate-600" data-id="element-1588" data-mp="2e09fic7gunhr">{d.reg}</p>
                </div>
                <div className="text-right" data-id="element-1589" data-mp="291pwajk9bpt3">
                  <p className={`font-heading text-lg font-bold leading-none ${t.tone === 'red' ? 'text-red-700' : 'text-amber-700'}`} data-id="element-1590" data-mp="2gm0gqh9s4azv">{toBn(Math.abs(d.daysLeft ?? 0))}</p>
                  <p className="text-[10px] text-slate-500" data-id="element-1591" data-mp="2qd4whj7a3zvr">{(d.daysLeft ?? 0) < 0 ? 'দিন আগে শেষ' : 'দিন বাকি'}</p>
                </div>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[11.5px]" data-id="element-1592" data-mp="2vb6dlyl59p4l">
                <span className="text-slate-600" data-id="element-1593" data-mp="1xpqtp4gwga3q">মেয়াদ: <b data-id="element-1594" data-mp="22naiuzf4y7ag">{d.expiry}</b></span>
                <StatusBadge tone={t.tone} className="!text-[10px]" data-id="element-1595" data-mp="22obvv9fpp3ut">{t.label}</StatusBadge>
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-2" data-id="element-1596" data-mp="27lkflegidfy6">
                <button onClick={() => {
              nav.setVehicleId(d.vid);
              nav.go('docTracker');
            }} className="flex items-center justify-center gap-1 rounded-lg bg-white py-2 text-[12px] font-semibold text-navy-800" data-id="element-1597" data-mp="2cl6v5ujt78a5"><Eye className="h-3.5 w-3.5" data-id="element-1598" data-mp="3o0lrcw471w47" />ডকুমেন্ট দেখুন</button>
                <button onClick={() => {
              nav.setVehicleId(d.vid);
              nav.setCorrectionPrefill(`doc:${d.name}:${d.expiry}`);
              nav.go('correction');
            }} className="flex items-center justify-center gap-1 rounded-lg bg-navy-800 py-2 text-[12px] font-semibold text-white" data-id="element-1599" data-mp="3nysj5cdxn3u3"><FilePen className="h-3.5 w-3.5" data-id="element-1600" data-mp="1wzk9f8t6ly6w" />হালনাগাদের আবেদন করুন</button>
              </div>
            </div>;
      })}
        {list.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-500" data-id="element-1601" data-mp="1svchl2rfg24g">এই সময়ের মধ্যে কোনো ডকুমেন্টের মেয়াদ শেষ হচ্ছে না</p>}
      </div>
    </OwnerShell>;
}